import { useState } from "react";
import { ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { describeLogin } from "../lib/format";
import { ErrorBanner } from "../components/ui";

/**
 * Shown on first sign-in to an account somebody else made. The password they
 * were given is shared, so until they choose their own nothing else opens —
 * here, and on the server.
 */
export default function ChoosePasswordPage() {
  const { user, chooseOwnPassword, signOut } = useAuth();
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== again) {
      setError("The two passwords are not the same.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await chooseOwnPassword(password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <span className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-xl bg-accent-600 text-lg font-bold text-white">
            ₹
          </span>
          <h1 className="text-xl font-semibold text-ink-950">
            Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Choose your own password to finish setting up{" "}
            {user?.email ? <span className="whitespace-nowrap">{describeLogin(user.email)}</span> : "your account"}.
          </p>
        </div>

        <div className="card-surface p-6">
          {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
          <p className="mb-4 text-sm text-ink-600">
            The password you signed in with was given to you by someone else. Pick one only you know — you
            will use it from now on.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label" htmlFor="new-password">New password</label>
              <input
                id="new-password"
                className="input"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="label" htmlFor="new-password-again">Type it again</label>
              <input
                id="new-password-again"
                className="input"
                type="password"
                required
                minLength={8}
                value={again}
                onChange={(e) => setAgain(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <button className="btn-primary w-full" disabled={busy}>
              {busy ? "Saving…" : "Save and continue"}
            </button>
          </form>
          <p className="mt-4 text-center text-sm text-ink-500">
            <button className="text-accent-700 underline" onClick={signOut}>Sign out</button>
          </p>
        </div>
      </div>
    </div>
  );
}
